import React, { useEffect, useState } from "react";
import { Button, FileIcon } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { MiniAttrGrid } from "#common/MiniAttrGrid";
import * as styles from "./styles_vanilla.css";
import type { GlobalAttribute, QuarantineItem, ReferenceData } from "#common/types/vault";
import type { DocumentReviewQueueProps } from "./types";

export type { DocumentReviewQueueProps };

const PREVIEWABLE = ["pdf", "doc", "docx", "txt"];

type Severity = "warning" | "danger";
type Problem =
  | { type: "missing"; fields: string[]; severity: Severity }
  | { type: "noroute"; severity: Severity }
  | { type: "validation"; field: string; severity: Severity }
  | { type: "other"; severity: Severity };

function classify(item: QuarantineItem): Problem {
  const p = item.problem || "";
  if (p.startsWith("Missing:")) {
    const fields = p
      .slice(8)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return { type: "missing", fields, severity: "warning" };
  }
  if (p.startsWith("No space matched")) return { type: "noroute", severity: "danger" };
  if (p.startsWith("Failed validation")) {
    const field = p.split(":")[1]?.trim() ?? "rule";
    return { type: "validation", field, severity: "danger" };
  }
  return { type: "other", severity: "warning" };
}

type Editor =
  | { kind: "select"; options: string[] }
  | { kind: "multi-select"; options: string[] }
  | { kind: "text" };

function editorFor(attr: GlobalAttribute, reference?: ReferenceData): Editor {
  const dt = attr.dataType;
  if (dt === "Yes/No") return { kind: "select", options: ["Yes", "No"] };
  if (dt === "Single-select") return { kind: "select", options: attr.values ?? [] };
  if (dt === "Multi-select") return { kind: "multi-select", options: attr.values ?? [] };
  if (dt === "Reference" && reference) {
    const map: Record<string, string[] | undefined> = {
      client: reference.clients,
      fundAdmin: reference.fundAdmins,
      investor: reference.investors,
      period: reference.periods,
    };
    const opts = map[attr.key];
    if (opts) return { kind: "select", options: opts };
  }
  return { kind: "text" };
}

export function DocumentReviewQueue({
  item,
  space = null,
  globalLib = [],
  reference,
  onResolve,
  onClose,
}: DocumentReviewQueueProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!item) return null;

  const problem = classify(item);
  const missingFields = problem.type === "missing" ? problem.fields : [];
  const validationField = problem.type === "validation" ? problem.field : null;

  const headlineText =
    problem.type === "missing"
      ? `Missing ${missingFields.length} attribute${missingFields.length === 1 ? "" : "s"}`
      : problem.type === "noroute"
        ? "No space matched"
        : problem.type === "validation"
          ? `Failed validation: ${problem.field}`
          : item.problem;
  const severityColor = problem.severity === "danger" ? "#b61c3a" : "#c8421e";

  const onGlobals = space ? globalLib.filter((a) => space.globals[a.key]?.on) : [];
  const canPreview = PREVIEWABLE.includes((item.file || "").toLowerCase());
  const canResolve = missingFields.every((f) => (values[f] ?? "").trim() !== "");
  const ext = (item.file || "doc").toLowerCase();

  const setVal = (name: string, v: string) => setValues((prev) => ({ ...prev, [name]: v }));

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden />

      <aside className={styles.previewPane}>
        <div className={styles.previewHead}>
          <div className={styles.previewName} title={item.name}>
            {item.name}
          </div>
          <Button
            variant="neutral"
            icon="download"
            iconPosition="left"
            onClick={() => undefined}
          >
            Download
          </Button>
        </div>
        <div className={styles.previewBody}>
          {canPreview ? (
            <div className={styles.page}>
              <div className={styles.letterhead}>
                <div className={styles.lhMark}>{space ? space.name : "Unfiled document"}</div>
                <div className={styles.lhMeta}>{item.arrived}</div>
              </div>
              <h1 className={styles.pageTitle}>{item.name}</h1>
              <div className={styles.rule} />
              {[70, 92, 84, 78, 66].map((w, i) => (
                <div key={i} className={styles.line} style={{ width: `${w}%` }} />
              ))}
            </div>
          ) : (
            <div className={styles.noPreview}>
              <div className={styles.noPreviewIcon}>
                <Icon type="eye-slash" size="xxx-large" />
              </div>
              <div className={styles.noPreviewTitle}>No preview available</div>
              <div className={styles.noPreviewText}>
                {ext.toUpperCase()} files can’t be previewed in the Vault. Download the file to
                inspect it before resolving.
              </div>
              <Button
                variant="neutral"
                icon="download"
                iconPosition="left"
                onClick={() => undefined}
              >
                Download to inspect
              </Button>
            </div>
          )}
        </div>
      </aside>

      <aside className={styles.drawer} role="dialog" aria-label="Resolve document">
        <div className={styles.head}>
          <FileIcon extension={ext} size="medium" />
          <div className={styles.headText}>
            <h2 className={styles.headTitle} title={item.name}>
              {item.name}
            </h2>
            <div className={styles.headline}>
              <span style={{ display: "inline-flex", color: severityColor }}>
                <Icon type="exclamation-triangle" size="small" customColor={severityColor} />
              </span>
              <span>{headlineText}</span>
            </div>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <Icon type="xmark" size="medium" />
          </button>
        </div>

        <div className={styles.bodyArea}>
          {space ? (
            <>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelHeadTitle}>Global attributes</h3>
                </div>
                <div className={styles.panelBody}>
                  <MiniAttrGrid
                    rows={onGlobals.map((a) => ({
                      key: a.name,
                      scope: "global" as const,
                      attribute: a.name,
                      value: values[a.name] ?? "",
                      editable: missingFields.includes(a.name),
                      editor: editorFor(a, reference),
                    }))}
                    onEdit={(row, v) => setVal(row.key, v)}
                  />
                </div>
              </div>

              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelHeadTitle}>Space-local attributes</h3>
                </div>
                <div className={styles.panelBody}>
                  {space.locals.length > 0 ? (
                    <MiniAttrGrid
                      showSpace
                      rows={space.locals.map((l) => ({
                        key: l.id,
                        scope: "local" as const,
                        attribute: l.name,
                        space: space.name,
                        value: validationField === l.name ? "Failed check" : "",
                      }))}
                    />
                  ) : (
                    <div className={styles.attrEmpty}>
                      No space-local attributes defined by {space.name}.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.panel}>
              <div className={styles.panelBody}>
                <div className={styles.attrEmpty}>
                  Attributes appear once this document is filed into a space.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.foot}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="check"
            iconPosition="left"
            disabled={!canResolve}
            onClick={() => onResolve(item.id)}
          >
            Resolve &amp; file
          </Button>
        </div>
      </aside>
    </>
  );
}
